import React, { useState } from 'react';
import { billingAPI, type PublicUser } from '../../utils/api';
import { type YooKassaPaymentMethod, yooKassaMethodLabel } from './yookassaPaymentMethods';
import './TopUpModal.css';

const PRESET_AMOUNTS = [500, 1000, 3000, 5000];
const PENDING_PAYMENT_KEY = 'yookassa_pending_payment_id';

interface TopUpModalProps {
  currentBalance: number;
  paymentMethodType?: YooKassaPaymentMethod | null;
  onClose: () => void;
  onSuccess?: (balance: number, user?: PublicUser) => void;
}

function submitLabel(method: YooKassaPaymentMethod | null | undefined, loading: boolean): string {
  if (loading) return 'Переход к оплате…';
  if (!method) return 'Оплатить через ЮKassa';
  return `Оплатить через ${yooKassaMethodLabel(method)}`;
}

const TopUpModal: React.FC<TopUpModalProps> = ({
  currentBalance,
  paymentMethodType = null,
  onClose,
}) => {
  const [amount, setAmount] = useState<number>(1000);
  const [customAmount, setCustomAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const selectedAmount = customAmount.trim()
    ? Number(customAmount.replace(/\s/g, '').replace(',', '.'))
    : amount;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!Number.isFinite(selectedAmount) || selectedAmount < 1) {
      setError('Минимальная сумма пополнения — 1 ₽');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await billingAPI.topUp(selectedAmount, paymentMethodType);
      if (response.success && response.confirmation_url) {
        if (response.payment_id) {
          sessionStorage.setItem(PENDING_PAYMENT_KEY, response.payment_id);
        }
        window.location.href = response.confirmation_url;
        return;
      }
      setError(response.message || 'Не удалось создать платёж');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Не удалось создать платёж');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="topup-modal" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="topup-modal__dialog" onClick={(e) => e.stopPropagation()}>
        <h2 className="topup-modal__title">Пополнить баланс</h2>
        <p className="topup-modal__balance">
          Текущий баланс: <strong>{currentBalance.toLocaleString('ru-RU')} ₽</strong>
        </p>
        {paymentMethodType && (
          <p className="topup-modal__hint">
            После подтверждения откроется оплата через {yooKassaMethodLabel(paymentMethodType)}
          </p>
        )}

        <form onSubmit={(e) => void handleSubmit(e)}>
          <div className="topup-modal__presets">
            {PRESET_AMOUNTS.map((preset) => (
              <button
                key={preset}
                type="button"
                className={`topup-modal__preset${amount === preset && !customAmount ? ' topup-modal__preset--active' : ''}`}
                onClick={() => {
                  setAmount(preset);
                  setCustomAmount('');
                }}
              >
                {preset.toLocaleString('ru-RU')} ₽
              </button>
            ))}
          </div>

          <label className="topup-modal__field">
            <span>Другая сумма</span>
            <input
              type="text"
              inputMode="decimal"
              placeholder="Например, 2500"
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
            />
          </label>

          {error && <p className="topup-modal__error">{error}</p>}

          <button type="submit" className="topup-modal__submit" disabled={loading}>
            {submitLabel(paymentMethodType, loading)}
          </button>
        </form>
      </div>
    </div>
  );
};

export default TopUpModal;
export { PENDING_PAYMENT_KEY };
export type { YooKassaPaymentMethod };
